import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { Location, History } from 'history';
import { useSelector, useDispatch } from 'react-redux';
import capitalize from 'lodash.capitalize';
import pluralize from 'helpers/pluralize';
import styled from 'styled-components/macro';

import getFiltersFromUrl from 'helpers/getFiltersFromUrl';
import {
  EntityContext,
  defaultColors,
  IEntity,
  CompareModes,
  ContextTopics,
  EntityTypes,
} from 'context/entityContext';
import getSchemas from 'schemas/componentSettings/viewSettings';
import useComponentSettings from 'hooks/useComponentSettings';
import { loadEntity } from 'redux/reducers/entity';
import goTo from 'components/Header/CompareDropdown/utils/goTo';

import { ReactComponent as IconPlus } from 'icons/icon-plus.svg';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import ScrollTopOnMount from 'components/utility/ScrollTopOnMount/ScrollTopOnMount';
import TopBar from 'components/Header/TopBar/TopBar';
import Header from 'components/Header/Header';
import InViewObserver from 'components/utility/InViewObserver/InViewObserver';
import BackToTop from 'components/BackToTop/BackToTop';
import Footer from 'components/Footer/Footer';
import NotificationSettingsModal from 'components/notifications/Settings';
import Message from 'components/base/Message/Message';
import Layout from 'components/styled/Layout/Layout';
import { Wrapper } from 'components/Header/CountryCompareHeader/CountryCompareHeader';
import {
  Header as TitleHeader,
  Title,
  closeLinkStyle,
} from 'components/Header/EntityCompareHeader/EntityCompareHeader';
import CompareSearch from 'components/Header/EntityCompareHeader/CompareSearch/CompareSearch';
import { ToggleSearchButton } from 'components/Header/EntityCompareHeader/EntityCompareHeader';
import CompareList from 'components/Header/EntityCompareHeader/CompareList/CompareList';
import { RootState } from 'redux/rootReducer';
import { ParentTopicTypes, SubTopicTypes } from 'topics';
import {
  loadCustomRegions,
  shouldLoadCustomRegions,
} from 'redux/reducers/customRegions';
import { isArray } from 'lodash';

const MessageContent = styled(Layout.MaxWidth)`
  margin: 0.5 0;
`;

const CloseButton = styled('button')<any>`
  ${closeLinkStyle};
  cursor: pointer;
`;

interface IEntityCompareIntermediate {
  compareMode: CompareModes;
  history: History;
  location: Location;
  match: {
    params: {
      topic: ParentTopicTypes;
      subTopic: SubTopicTypes;
      parentEntityId: any;
      entityType: EntityTypes;
      countries: any;
      entityId: string;
    };
  };
}

const EntityCompareIntermediate: React.FC<IEntityCompareIntermediate> = (
  props
) => {
  const { compareMode, history, location, match } = props;
  const {
    topic,
    subTopic,
    parentEntityId,
    entityType,
    entityId,
    countries,
  } = match.params;

  const defaultTopic = 'performance';
  const defaultSubTopic = 'streaming';
  const isCountryCompare = useMemo(() => compareMode === 'country', [
    compareMode,
  ]);
  const [isSearchVisible, setSearchVisibility] = useState<any>({
    country: false,
    entity: false,
  });

  //Redux state
  const entitiesById = useSelector(
    (state: RootState) => state.entity.byId[entityType]
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (dispatch(shouldLoadCustomRegions())) {
      dispatch(loadCustomRegions());
    }
  }, [dispatch]);

  const customRegions = useSelector(
    (state: RootState) => state.customRegions.data
  );

  // view settings
  const schema = getSchemas();
  const [viewSettings] = useComponentSettings('viewSettings', schema);

  useEffect(() => {
    setSearchVisibility({
      ...isSearchVisible,
      [isCountryCompare ? 'country' : 'entity']: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCountryCompare]);

  /** Once the entity is chosen we want to load the entity data */
  useEffect(() => {
    if (entityType && entityId) {
      dispatch(loadEntity(entityType, entityId, entityType !== 'label'));
    }
  }, [dispatch, entityId, entityType]);

  const isHeaderLower =
    location.pathname.indexOf('/territory') !== -1 || entityType === 'label';
  const entityIds = entityId ? [entityId] : [];
  const topicPath: ContextTopics = [
    topic || defaultTopic,
    subTopic || defaultSubTopic,
  ];

  const customRegionFilter =
    getFiltersFromUrl(location)?.customRegion && !isArray(customRegions)
      ? getFiltersFromUrl(location)?.customRegion
      : undefined;

  const context: IEntity = {
    entityType,
    entityIds,
    countryIds: countries && countries !== 'all' ? countries.split(',') : [],
    compareMode,
    topic: topicPath,
    parentEntityId,
    filters: getFiltersFromUrl(location),
    viewSettings,
    isVersion: false, // @TODO add compare mode for versions
    dataColors: defaultColors.compareColors,
    customRegionFilter,
  };

  const toggleSearchVisibility = (type: any): void =>
    setSearchVisibility({ ...isSearchVisible, [type]: !isSearchVisible[type] });
  const getMessage = useCallback(
    ({
      entityType,
      isCountryCompare,
    }: {
      entityType: string;
      isCountryCompare: boolean;
    }) => {
      return isCountryCompare
        ? `To compare countries, please add a ${entityType}.`
        : `You need at least 2 ${pluralize(
            entityType
          )} to show comparison by ${entityType}.`;
    },
    []
  );
  /** To reload the entity data in case of error or load it first time */
  const reloadEntity = (entityId: string): void => {
    dispatch(loadEntity(entityType, entityId, entityType !== 'label'));
  };

  return (
    <>
      <ScrollTopOnMount identifier={`compareModeIntermediate/${compareMode}`} />
      <ErrorBoundary type="page">
        {entityType && (
          <Helmet
            defer={false}
            title={`${compareMode} - ${capitalize(entityType)}`}
          />
        )}
        <EntityContext.Provider value={context}>
          <InViewObserver
            render={(props: any): ReactNode => (
              <Header
                loading={false}
                hasStickyTopBar
                inView={props.inview && isHeaderLower}
              >
                <TopBar inView={props.inview} isSticky noComparedItems />
                <Layout.MaxWidth>
                  <Wrapper>
                    <TitleHeader>
                      <Title>
                        {isCountryCompare
                          ? `Compare countries for a${
                              entityType === 'artist' ? 'n' : ''
                            } ${entityType}`
                          : `Compare ${pluralize(entityType)}`}
                      </Title>
                      <CloseButton
                        onClick={(): void => goTo({ history, entityId })}
                      >
                        Close compare
                      </CloseButton>
                    </TitleHeader>

                    {/** Entity compare */}
                    {entitiesById && entityId && entitiesById[entityId] && (
                      <CompareList
                        entityIds={entityIds}
                        entityType={entityType}
                        entitiesById={entitiesById}
                        topic={topicPath}
                        reloadEntity={reloadEntity}
                      />
                    )}
                    {(isSearchVisible.entity ||
                      (isSearchVisible.country && !entityId)) && (
                      <CompareSearch
                        entityIds={entityIds}
                        entityType={entityType}
                        topic={topicPath}
                        history={history}
                        toggleSearchVisibility={(): void =>
                          toggleSearchVisibility(
                            isCountryCompare ? 'country' : 'entity'
                          )
                        }
                        index={entityIds.length}
                        compareMode={compareMode}
                      />
                    )}
                    {!isSearchVisible.entity && !isSearchVisible.country && (
                      <ToggleSearchButton
                        onClick={(): void =>
                          toggleSearchVisibility(
                            isCountryCompare ? 'country' : 'entity'
                          )
                        }
                      >
                        <IconPlus /> Add {entityType} to compare
                      </ToggleSearchButton>
                    )}
                  </Wrapper>
                </Layout.MaxWidth>
              </Header>
            )}
          />
        </EntityContext.Provider>

        <MessageContent>
          <Message>{getMessage({ entityType, isCountryCompare })}</Message>
        </MessageContent>

        <NotificationSettingsModal />
        <BackToTop />
        <Footer />
      </ErrorBoundary>
    </>
  );
};

export default EntityCompareIntermediate;
